import React, { useState, useEffect, useRef } from 'react';

function extractBunnyIds(url) {
  const match = url.match(/iframe\.mediadelivery\.net\/(?:play|embed)\/(\d+)\/([a-f0-9-]+)/);
  return match ? { libraryId: match[1], videoId: match[2] } : null;
}

const VideoPlayer = ({ onVideoComplete, videoUrl }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [videoEnded, setVideoEnded] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    if (window.playerjs) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://assets.mediadelivery.net/playerjs/player-0.1.0.min.js';
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (!scriptLoaded || !iframeRef.current || playerRef.current) return;

    const initPlayer = () => {
      if (!window.playerjs) {
        setTimeout(initPlayer, 100);
        return;
      }

      const player = new window.playerjs.Player(iframeRef.current);
      playerRef.current = player;

      player.on('ready', () => {
        setIsLoading(false);
        // El botón se habilita cuando el video termina (evento `ended`)...
        player.on('ended', () => setVideoEnded(true));
        // ...o cuando el progreso llega a ~99%. Fallback robusto: en algunos
        // navegadores mobile el evento `ended` no dispara y el usuario quedaba
        // atascado sin poder continuar. timeupdate sí dispara de forma fiable
        // en Bunny (verificado: {seconds, duration}). No permite saltar el video.
        player.on('timeupdate', (e) => {
          if (e && e.duration > 0 && e.seconds / e.duration >= 0.99) {
            setVideoEnded(true);
          }
        });
      });
    };

    initPlayer();
  }, [scriptLoaded]);

  const getBunnyEmbedUrl = () => {
    if (!videoUrl) return '';
    const ids = extractBunnyIds(videoUrl);
    if (!ids) return videoUrl;
    return `https://iframe.mediadelivery.net/embed/${ids.libraryId}/${ids.videoId}?autoplay=true&preload=true`;
  };

  return (
    <div className="text-center py-8">
      <div className="relative w-full max-w-5xl mx-auto mb-6 rounded-xl overflow-hidden shadow-2xl bg-black aspect-video">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
            <div className="text-center">
              <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-gray-400 text-sm">Cargando video...</p>
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={getBunnyEmbedUrl()}
          className="absolute inset-0 w-full h-full"
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture; accelerometer; gyroscope"
          allowFullScreen
          playsInline
          title="Video de Bienvenida"
        />
      </div>

      <button
        onClick={() => videoEnded && onVideoComplete()}
        disabled={!videoEnded}
        className={`inline-flex items-center gap-2 font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg ${
          videoEnded
            ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black transform hover:scale-105 hover:shadow-yellow-500/50'
            : 'bg-gray-800 text-gray-600 cursor-not-allowed'
        }`}
      >
        {videoEnded ? 'Continuar' : 'Ve el video para continuar'}
      </button>
      {!videoEnded && (
        <p className="text-xs text-gray-500 mt-2">El botón se habilitará al terminar el video</p>
      )}
    </div>
  );
};

export default VideoPlayer;